import { action, computed, observable } from 'mobx'
import { type IValidationSchema, required, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach, newCid } from '@/features/legends/editor/collections'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import type { QuestionEditorViewModelBase } from '@/features/legends/questions/QuestionEditorViewModelBase'
import { validateAll, validateElements } from '@/utils/validx-util'
import { MappingEditorViewModel } from './MappingEditorViewModel'
import { type Template, templateDataSources } from './templates'

/**
 * A scoping template: one mapping for each question that can be answered.
 */
export class TemplateEditorViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<TemplateEditorViewModel> = {
    name: [required('Name is required')],
    mappings: [validateElements('There are invalid mappings.')],
  }

  readonly cid = newCid('template')

  @observable accessor id = ''
  @observable accessor name = 'Template'

  @observable.shallow accessor mappings: MappingEditorViewModel[] = []
  @observable.ref accessor selectedMappingVm: MappingEditorViewModel | null =
    null

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies) {
    super(dependencies)
    this.id = dependencies.symbols.newId(this.cid, 'TEMPLATE') ?? ''
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.mappings))
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  @computed
  private get _json(): Template {
    return {
      id: this.id,
      name: this.name,
      mappings: this.mappings.map((m) => m.toJSON()),
      // Saved data sources are ignored when loading, so older templates
      // pick up new ones.
      dataSources: templateDataSources,
    }
  }

  /**
   * Keeps one mapping per relevant question, reusing existing mappings. The
   * selection is cleared when its mapping is dropped.
   */
  @action
  updateMappings(questions: QuestionEditorViewModelBase[]) {
    const existing = new Map(this.mappings.map((m) => [m.questionId, m]))
    this.mappings = relevantQuestions(questions).map(
      (question) =>
        existing.get(question.id) ??
        new MappingEditorViewModel(this.dependencies, this, question),
    )
    if (
      this.selectedMappingVm &&
      !this.mappings.includes(this.selectedMappingVm)
    ) {
      this.selectedMappingVm = null
    }
    validateAll(this)
  }

  /**
   * Selects the mapping, to edit it in the inspector.
   */
  @action.bound
  edit(mapping: MappingEditorViewModel) {
    validateAll(this)
    this.selectedMappingVm = mapping
  }

  /**
   * Sets the template from the legend document. Mappings of questions that
   * no longer exist are dropped.
   *
   * @param template
   * @param questions Every question of the legend.
   */
  @action
  hydrate(template: Template, questions: QuestionEditorViewModelBase[]) {
    const relevant = relevantQuestions(questions)
    this.mappings = (template.mappings ?? []).flatMap((m) => {
      const question = relevant.find((q) => q.id === m.questionId)
      if (!question) {
        return []
      }
      const mapping = new MappingEditorViewModel(
        this.dependencies,
        this,
        question,
      )
      mapping.hydrate(m)
      return [mapping]
    })
    if (template.id) {
      this.id = template.id
    }
    this.name = template.name ?? 'Template'
  }

  /**
   * Shows the analyzer's errors on the template and its mappings.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    addGenericErrorForProperty(
      this,
      descriptor,
      'mappings',
      'There are invalid mappings.',
    )
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The template for the legend document.
   */
  toJSON(): Template {
    return this._json
  }
}

function relevantQuestions(questions: QuestionEditorViewModelBase[]) {
  return questions.filter(
    (q) =>
      q.type !== 'Breaker' && q.type !== 'Documents' && !q.info.isMetaElement,
  )
}
